<template>
    <svg class="area-chart" :width="width" :height="height" :viewBox="'0 0 '+width+' '+height">
        <defs>
            <linearGradient x1="53.1691595%" y1="102.685254%" x2="53.1691611%" y2="45.5346704%" id="linearGradient-1">
                <stop stop-color="#50BEF7" offset="0%"></stop>
                <stop stop-color="#2A94F0" offset="100%"></stop>
            </linearGradient>
        </defs>

        <g :transform="'translate('+margin.left+','+margin.top+')'">
            <g class="yAxis" ref="yAxis"></g>
            <g class="xAxis" ref="xAxis" :transform="'translate(0,'+chartHeight+')'"></g>
            <g v-if="data && data.length">
                <path :d="d" fill="url(#linearGradient-1)"></path>
                <path :d="lineD" fill="none" stroke="#1D92E0" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round"></path>
            </g>
            <g>
                <g v-for="point in data" :key="point.date" 
                   @mouseenter="selectedItem = point" 
                   @mouseleave="selectedItem = null" 
                   v-tooltip :title="formatTooltip(point)">
                    <circle r="6"
                            :cx="x(parseDate(point.date))" 
                            :cy="y(point.value)" 
                            fill="#FFFFFF" 
                            :opacity="point === selectedItem ? 1 : 0"></circle>
                    <circle r="4" 
                            :cx="x(parseDate(point.date))" 
                            :cy="y(point.value)" 
                            fill="#1D92E0" 
                            :opacity="point === selectedItem ? 1 : 0"></circle>
                </g>
            </g>
        </g>
    </svg>
</template>

<script>
import * as d3 from 'd3';
import moment from 'moment';
import 'moment/locale/es';
import Tooltip from '../directives/Tooltip.js';

export default {
    name: 'AreaChart',
    directives: {
        Tooltip
    },
    props: {
        data: {
            type: Array,
            required: true,
            default: () => []
        },
        width: {
            type: Number,
            required: true
        },
        height: {
            type: Number,
            required: true
        },
        yTickFormat: {
            type: String,
            default: "DD-MM-YY"
        }
    },
    created() {
        moment.locale('es');
    },
    data() {
        return {
            margin: { 
                top: 20, 
                left: 70,
                right: 30, 
                bottom: 50 
            },
            selectedItem: null
        };
    },
    computed: {
        chartWidth() {
            return this.width - this.margin.left - this.margin.right;
        },
        chartHeight() {
            return this.height - this.margin.top - this.margin.bottom;
        },
        x() {
            const dates = this.data.map(d => this.parseDate(d.date));
            return d3.scaleTime()
                .domain(d3.extent(dates))
                .range([0, this.chartWidth])
                .nice();
        },
        y() {
            const maxValue = d3.max(this.data, d => d.value) || 0;
            return d3.scaleLinear()
                .domain([0, maxValue * 1.1])
                .range([this.chartHeight, 0])
                .nice();
        },
        d() {
            if (!this.data || !this.data.length) return '';
            
            return d3.area()
                .x(d => this.x(this.parseDate(d.date)))
                .y1(d => this.y(d.value))
                .y0(this.y(0))
                .curve(d3.curveMonotoneX)(this.data);
        },
        lineD() {
            if (!this.data || !this.data.length) return '';
            
            return d3.line()
                .x(d => this.x(this.parseDate(d.date)))
                .y(d => this.y(d.value))
                .curve(d3.curveMonotoneX)(this.data);
        }
    },
    methods: {
        parseDate(dateStr) {
            if (this.yTickFormat === "YYYY") {
                return moment(dateStr, "YYYY").toDate();
            } else if (this.yTickFormat === "MMM YY") {
                // Para el formato "Apr 2024"
                const parsed = moment(dateStr, "MMM YYYY", 'en'); // Usar locale inglés para parsear
                return parsed.toDate();
            }
            return moment(dateStr).toDate();
        },
        formatValue(value) {
            if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + 'M';
            } else if (value >= 1000) {
                return (value / 1000).toFixed(1) + 'K';
            }
            return value.toString();
        },
        formatTooltip(point) {
            const formattedValue = this.formatValue(point.value);
            let date;
            if (this.yTickFormat === "MMM YY") {
                date = moment(this.parseDate(point.date)).format('MMMM YYYY');
            } else {
                date = point.date;
            }
            return `${formattedValue} visitas<br />${date}`;
        },
        generateAxis() {
            if (!this.data || !this.data.length) return;

            const yAxis = d3.axisLeft(this.y)
                .tickSize(-this.chartWidth)
                .ticks(5)
                .tickFormat(d => this.formatValue(d));

            let xAxis;
            if (this.yTickFormat === "YYYY") {
                xAxis = d3.axisBottom(this.x)
                    .ticks(d3.timeYear.every(1))
                    .tickFormat(d => moment(d).format('YYYY'));
            } else if (this.yTickFormat === "MMM YY") {
                const monthInterval = Math.ceil(this.data.length / 12) || 1;
                xAxis = d3.axisBottom(this.x)
                    .ticks(d3.timeMonth.every(1))
                    .tickFormat(d => moment(d).format('MMM YY'));
            } else {
                const numberOfTicks = Math.min(this.data.length, 30);
                xAxis = d3.axisBottom(this.x)
                    .ticks(numberOfTicks)
                    .tickFormat(d => moment(d).format('DD-MM-YY'));
            }

            d3.select(this.$refs.yAxis)
                .call(yAxis)
                .call(g => g.selectAll('.tick line')
                    .attr('stroke-opacity', 0.3));

            d3.select(this.$refs.xAxis)
                .call(xAxis)
                .call(g => g.selectAll('text')
                    .attr('transform', 'rotate(-45)')
                    .style('text-anchor', 'end')
                    .attr('dx', '-0.8em')
                    .attr('dy', '0.15em'));
        }
    },
    watch: {
        data: {
            immediate: true,
            deep: true,
            handler() {
                this.$nextTick(this.generateAxis);
            }
        },
        width() {
            this.$nextTick(this.generateAxis);
        }
    },
    mounted() {
        this.generateAxis();
    }
}
</script>

<style lang="scss" scoped>
.area-chart {
    .xAxis {
        .tick {
            text {
                fill: #7f8080;
                font-size: 10px;
            }
            line {
                stroke: #006BB0;
            }
        }
        .domain {
            stroke: #B5B6B5;
        }
    }
    .yAxis {
        .tick {
            text {
                fill: #7f8080;
                font-size: 10px;
            }
            line {
                stroke: #F5F4F4;
            }
        }
        .domain {
            display: none;
        }
    }
    circle {
        cursor: pointer;
        transition: opacity 0.2s ease;
    }
}
</style>